import React from 'react';
import { AlertTriangle, RefreshCw, Settings, Info } from 'lucide-react';

const AnalysisError = ({ error, onRetry, onSwitchToBasic }) => {
  const isAdvancedAnalysisError = error?.details?.includes('Advanced analysis failed') || 
                                  error?.includes('Advanced analysis failed');

  return (
    <div className="max-w-4xl mx-auto">
      <div className="card border-red-200 bg-red-50">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <div className="ml-4 flex-1">
            <h2 className="text-xl font-semibold text-red-900 mb-2">
              Analysis Failed
            </h2>
            
            {isAdvancedAnalysisError ? (
              <div className="space-y-4">
                <div className="text-red-800">
                  <p className="mb-2">
                    <strong>Advanced Analysis Error:</strong> The Gemini AI analysis could not be completed.
                  </p>
                  <p className="mb-3">
                    This usually happens when:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                    <li>Gemini API key is not configured or invalid</li>
                    <li>API quota limits have been reached</li>
                    <li>Video content cannot be accessed by the AI</li>
                    <li>Network connectivity issues</li>
                  </ul>
                </div>

                <div className="bg-white border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <Info className="w-4 h-4 mr-2" />
                    Solutions:
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                        1
                      </span>
                      <div>
                        <p className="font-medium text-gray-900">Try Basic Analysis</p>
                        <p className="text-sm text-gray-600">Use the standard analysis which works without Gemini AI</p>
                        <button
                          onClick={onSwitchToBasic}
                          className="mt-2 btn-secondary text-sm"
                        >
                          Switch to Basic Analysis
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                        2
                      </span>
                      <div>
                        <p className="font-medium text-gray-900">Check API Configuration</p>
                        <p className="text-sm text-gray-600">Verify your Gemini API key is correctly set in the server environment</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                        3
                      </span>
                      <div>
                        <p className="font-medium text-gray-900">Retry Analysis</p>
                        <p className="text-sm text-gray-600">Try the analysis again in case it was a temporary issue</p>
                        <button
                          onClick={onRetry}
                          className="mt-2 btn-primary text-sm"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Retry Analysis
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-800 mb-2 flex items-center">
                    <Settings className="w-4 h-4 mr-2" />
                    API Configuration Help:
                  </h4>
                  <p className="text-sm text-yellow-700 mb-2">
                    To use Advanced Analysis, you need a valid Gemini API key:
                  </p>
                  <ol className="list-decimal list-inside text-sm text-yellow-700 space-y-1">
                    <li>Get a Gemini API key from Google AI Studio</li>
                    <li>Add it to your server's <code className="bg-yellow-100 px-1 rounded">.env</code> file as <code className="bg-yellow-100 px-1 rounded">GEMINI_API_KEY</code></li>
                    <li>Restart your server</li>
                  </ol>
                </div>
              </div>
            ) : (
              <div className="text-red-800">
                <p className="mb-2">
                  <strong>Error:</strong> {error}
                </p>
                <p className="mb-4">
                  There was an issue processing your video. Please try again.
                </p>
                <button
                  onClick={onRetry}
                  className="btn-primary"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisError;
